import React from 'react';
import { Colors } from 'library';
import { Avatar, Card, Checkbox, Chip, ExpandableCard, Rating, Row, Stack, Text } from 'library/ui';
import { PermissionType, useUserContext } from 'UserContext';
import { ITeacher } from 'models';
import MessageWorkerButton from 'parent-portal/messages/MessageWorkerButton';

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export default function ShowcaseTeacherRow({
    worker,
    selected,
    onChange,
    showcaseHighlight,
    rightAction,
}: {
    worker: ITeacher;
    selected?: boolean;
    onChange?: (worker: ITeacher) => void;
    showcaseHighlight?: boolean;
    rightAction?: React.ReactNode;
}) {
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);
    const showCheckbox = canPostJobs && onChange;
    const reviews = worker.credentials.reviews;
    const hasReviews = reviews && reviews.average_score > 0;

    const header = (
        <Row justify="between" align="center" fullWidth>
            <Row gap="sm" align="center">
                {showCheckbox && (
                    <span onClick={stopPropagation}>
                        <Checkbox checked={!!selected} onChange={() => onChange!(worker)} />
                    </span>
                )}
                <Avatar imageUrl={worker.pictureUrl} size={48} name={worker.name} />
                <Stack gap="none">
                    <Row gap="sm" wrap>
                        <Text weight="bold">{worker.name}</Text>
                        {showcaseHighlight && (
                            <Chip variant="filled" bgColor={Colors.accentSoft} textColor={Colors.accent} size="small">
                                SHOWCASE
                            </Chip>
                        )}
                    </Row>
                    {worker.credentials.education.length > 0 && (
                        <Row gap="sm" align="baseline" wrap>
                            <Text size="sm" weight="bold">
                                Education:
                            </Text>
                            <Text size="sm" tone="secondary">
                                {worker.credentials.education.join(', ')}
                            </Text>
                        </Row>
                    )}
                    {worker.credentials.experience && (
                        <Row gap="sm" align="baseline" wrap>
                            <Text size="sm" weight="bold">
                                Experience:
                            </Text>
                            <Text size="sm" tone="secondary">
                                {worker.credentials.experience}
                            </Text>
                        </Row>
                    )}
                    {!!worker.credentials.highlights?.length && (
                        <Row gap="sm" wrap>
                            {worker.credentials.highlights.map((highlight) => (
                                <Chip
                                    key={highlight}
                                    variant="filled"
                                    bgColor={Colors.secondarySoft}
                                    textColor={Colors.secondary}
                                    size="small"
                                >
                                    {highlight}
                                </Chip>
                            ))}
                        </Row>
                    )}
                </Stack>
            </Row>
            <Row gap="md" align="center" wrap>
                <RatingContent reviews={reviews} hasReviews={hasReviews} />
                <MessageWorkerButton userId={worker.id} />
                {rightAction && <span onClick={stopPropagation}>{rightAction}</span>}
            </Row>
        </Row>
    );

    if (!worker.bio) {
        return <Card padding="sm">{header}</Card>;
    }

    return (
        <ExpandableCard padding="sm" header={header}>
            <Text size="sm" tone="secondary" lineHeight="relaxed">
                {worker.bio}
            </Text>
        </ExpandableCard>
    );
}

function RatingContent({
    reviews,
    hasReviews,
}: {
    reviews: { average_score: number; total_reviews: number } | null;
    hasReviews: boolean | null;
}) {
    return hasReviews && reviews ? (
        <Row gap="sm" align="center">
            <Rating value={reviews.average_score} readOnly precision={0.1} size="small" />
            <Text size="xs" tone="secondary">
                ({reviews.total_reviews})
            </Text>
        </Row>
    ) : (
        <Text size="xs" tone="secondary">
            No reviews yet
        </Text>
    );
}
